/**
 * FTR-COCKPIT: per-ship cockpit configuration (DATA ONLY).
 * No `three` import — plain numbers/interfaces so this stays unit-testable and
 * sim-core stays untouched. CockpitView consumes this to build materials/lights/
 * camera AND the procedural cabin frame (pillars/brow/consoles/dash/stack/bezels).
 * AAA-21: lights stay dim, no emissive anywhere.
 *
 * Frame geometry is authored for the eye-point at the origin (0,0,0) looking -Z
 * at ~72° fov: struts sit at the screen edges and the central canopy stays open.
 */

export interface CockpitLight {
  kind: 'directional' | 'ambient' | 'point';
  colorHex: number;
  intensity: number;
  /** Directional and point lights use this; ambient lights ignore it. */
  pos?: readonly [number, number, number];
  /** Point lights only: range past which the light reaches zero (0 = no cutoff). */
  distance?: number;
  /** Point lights only: physical falloff exponent (THREE default 2). */
  decay?: number;
}

export interface CockpitMaterials {
  canopyHex: number;
  dashHex: number;
  /** Shared defaults; per-material overrides below preserve today's exact look. */
  roughness: number;
  metalness: number;
  canopyRoughness?: number;
  canopyMetalness?: number;
  dashRoughness?: number;
  dashMetalness?: number;
}

/** Two raked A-pillars at the screen edges (mirrored ±x by `spread`). */
export interface PillarGeom {
  width: number;
  height: number;
  depth: number;
  /** Half-separation: each pillar sits at ±spread on x. */
  spread: number;
  /** Tilt-back of the pillar top (radians) so the canopy rakes away from the pilot. */
  rake: number;
  y: number;
  z: number;
}

/** Overhead brow beam spanning the pillar tops. */
export interface BrowGeom {
  width: number;
  height: number;
  depth: number;
  /** Chunky corner joints that carry the brow cleanly into the A-pillars. */
  cornerWidth: number;
  cornerHeight: number;
  cornerDepth: number;
  y: number;
  z: number;
}

/** Dash slab + cowl/steps/lip canted up toward the pilot. */
export interface DashGeom {
  width: number;
  height: number;
  depth: number;
  y: number;
  z: number;
  /** Front-lip cant (radians). */
  lip: number;
  /** Depth of the angled front lip. */
  lipDepth: number;
  hoodWidth: number;
  hoodHeight: number;
  hoodDepth: number;
  panelWidth: number;
  panelHeight: number;
  panelDepth: number;
  panelInset: number;
}

/** Side console, mirrored ±x by `x`, toed-in toward the centerline by `toe`. */
export interface ConsoleGeom {
  width: number;
  height: number;
  depth: number;
  x: number;
  y: number;
  z: number;
  /** Toe-in of the front face toward the centerline (radians). */
  toe: number;
  capHeight: number;
  faceHeight: number;
  faceDepth: number;
}

/**
 * One recessed, backlit instrument bay — the shared housing profile for ALL three
 * diegetic readouts so they read as ONE matched set. A bay is a flat inset screen
 * plate set back by `pocketDepth` behind a thin RAISED bezel lip that frames the
 * opening (the lip is the FRONTMOST geometry at this spot, so nothing occludes the
 * recessed screen). Reclined toward the eye by `pitch` and toed-in by `yaw`.
 * DATA-ONLY (no three import). cockpit.ts builds the housing; cockpitInstruments.ts
 * seats the unlit screen plane from the same numbers so the two always agree.
 */
export interface InstrumentBay {
  /** Outer bay width (cockpit units). */
  width: number;
  /** Outer bay height along its own (pre-rotation) Y. */
  height: number;
  /** How far the inset screen plate is recessed behind the bezel front face. */
  pocketDepth: number;
  /** Recline about X (radians, negative reclines the face up toward the eye). */
  pitch: number;
  /** Toe-in about Y (radians) for the flanking console bays; 0 for the centre. */
  yaw: number;
  /** Raised bezel lip border width on the face plane (the frame thickness). */
  bezelThickness: number;
  /** How far the raised bezel lip stands proud of the inset plate (along z). */
  bezelHeight: number;
  x: number;
  y: number;
  z: number;
}

/** The matched set of three recessed bays seated across the low instrument fascia:
 *  LEFT = power pips, CENTRE = velocity+throttle (just left of the centred radar),
 *  RIGHT = ship status. Every bay shares the same bezel/pocket profile. */
export interface InstrumentBays {
  left: InstrumentBay;
  centre: InstrumentBay;
  right: InstrumentBay;
}

/** Raised center stack between the consoles. */
export interface StackGeom {
  width: number;
  height: number;
  depth: number;
  y: number;
  z: number;
}

/** Instrument bezel ring seated on the dash face. */
export interface BezelGeom {
  x: number;
  y: number;
  z: number;
  radius: number;
  depth: number;
  seatRadius: number;
  seatDepth: number;
}

/** Full procedural-frame parameter set consumed by the builders in cockpit.ts. */
export interface CockpitGeometry {
  pillar: PillarGeom;
  brow: BrowGeom;
  dash: DashGeom;
  consoles: ConsoleGeom;
  centerStack: StackGeom;
  /** The three recessed instrument bays (one matched diegetic set). */
  bays: InstrumentBays;
  /**
   * Legacy dash bezel rings — retained as ship-config data (consumed by the
   * config contract tests) but no longer rendered: the recessed `bays` above
   * now provide every instrument housing as the frontmost dash geometry.
   */
  bezels: readonly BezelGeom[];
}

export interface CockpitConfig {
  id: string;
  name: string;
  fov: number;
  eyeOffset: readonly [number, number, number];
  materials: CockpitMaterials;
  lights: readonly CockpitLight[];
  /**
   * Procedural cabin-frame parameters. Optional: when omitted the builders fall
   * back to DEFAULT_COCKPIT_GEOMETRY. Both shipped configs populate it so every
   * ship gets a believable frame; skiff carries tuned ('Pallbearer') values.
   */
  geometry?: CockpitGeometry;
}

/** Matte charcoal, subtle relief split, no emissive or reflections. */
const BASE_MATERIALS: CockpitMaterials = {
  canopyHex: 0x141519,
  dashHex: 0x1b1e22,
  roughness: 0.92,
  metalness: 0.03,
  canopyRoughness: 0.94,
  canopyMetalness: 0.02,
  dashRoughness: 0.91,
  dashMetalness: 0.04,
};

/**
 * AAA-21 dim cabin rig: a soft upper-left KEY, a cooler weaker opposite FILL,
 * and a faint starboard rim/graze make the stepped dash and canopy joints read
 * without ever lighting the cabin up past the exterior. A faint cyan-tinted POINT
 * lamp seated just above the dash/centre adds a soft instrument-glow ambience so
 * the consoles read as lit FROM WITHIN, and the low ambient carries a subtle
 * --bm-primary (#3fd0e0) cyan bias to match the design-system phosphor. Σ stays
 * ≈0.82 so the cabin remains clearly darker than the void. No emissive, no glow —
 * the cockpit renders AFTER the bloom composer so these lamps never bloom anyway.
 */
const BASE_LIGHTS: readonly CockpitLight[] = [
  { kind: 'directional', colorHex: 0xd7dce0, intensity: 0.38, pos: [-0.68, 1.12, 0.82] },
  { kind: 'directional', colorHex: 0x657281, intensity: 0.1, pos: [0.82, 0.42, 0.58] },
  { kind: 'directional', colorHex: 0x4e5864, intensity: 0.08, pos: [0.94, -0.06, 0.18] },
  { kind: 'ambient', colorHex: 0x0c161b, intensity: 0.12 },
  // Faint interior phosphor lamp: cool cyan-tinted white seated above the dash/
  // centre stack, short-range so it grazes the consoles without spilling outside.
  { kind: 'point', colorHex: 0xcdebf0, intensity: 0.14, pos: [0, -0.24, -0.4], distance: 2.4, decay: 2 },
];

/**
 * Default frame: a believable shell at the origin eye-point / 72° fov. Raked
 * A-pillars frame the side edges, an overhead brow caps the top, mirrored side
 * consoles frame the lower corners, a dash + angled lip and three instrument
 * bezels sit below the sightline, and a raised center stack rises between the
 * consoles — all clear of the open central canopy you look out through.
 */
export const DEFAULT_COCKPIT_GEOMETRY: CockpitGeometry = {
  pillar: { width: 0.11, height: 2.0, depth: 0.14, spread: 0.62, rake: 0.16, y: 0.1, z: -0.74 },
  brow: {
    width: 1.46, height: 0.13, depth: 0.5, cornerWidth: 0.22, cornerHeight: 0.17, cornerDepth: 0.32,
    y: 0.55, z: -0.8,
  },
  dash: {
    width: 1.78, height: 0.32, depth: 0.86, y: -0.55, z: -0.6, lip: 0.48, lipDepth: 0.24,
    hoodWidth: 0.86, hoodHeight: 0.18, hoodDepth: 0.4, panelWidth: 0.28, panelHeight: 0.08,
    panelDepth: 0.3, panelInset: 0.06,
  },
  consoles: {
    width: 0.34, height: 0.7, depth: 0.9, x: 0.64, y: -0.42, z: -0.6, toe: 0.2,
    capHeight: 0.12, faceHeight: 0.18, faceDepth: 0.32,
  },
  centerStack: {
    // Trimmed + recessed: a low hump between the consoles whose front face sits
    // BEHIND the centre-left velocity bay so it never projects in front of it
    // (the do-not gate: nothing occludes a recessed screen). z front = -0.37.
    width: 0.3, height: 0.3, depth: 0.3, y: -0.34, z: -0.52,
  },
  // One matched set of recessed bays. Shared bezel/pocket profile across all three;
  // only placement (x/y/z) and pitch/yaw differ. Centre is COMPACT (value + slim
  // throttle bar); left/right are equal-sized secondaries flanking the radar.
  bays: {
    left: {
      width: 0.13, height: 0.108, pocketDepth: 0.028, pitch: -0.42, yaw: 0.34,
      bezelThickness: 0.012, bezelHeight: 0.014, x: -0.265, y: -0.02, z: -0.3,
    },
    // Compact: narrowed + shortened and nudged toward centre so its left edge
    // (x - width/2 = -0.165) clears the POWER bay's right edge (~-0.20) — no
    // overlap — while staying left of the centred radar.
    centre: {
      width: 0.14, height: 0.06, pocketDepth: 0.028, pitch: -0.42, yaw: 0,
      bezelThickness: 0.012, bezelHeight: 0.014, x: -0.095, y: -0.065, z: -0.285,
    },
    right: {
      width: 0.13, height: 0.108, pocketDepth: 0.028, pitch: -0.42, yaw: -0.34,
      bezelThickness: 0.012, bezelHeight: 0.014, x: 0.265, y: -0.02, z: -0.3,
    },
  },
  bezels: [
    { x: -0.36, y: -0.39, z: -0.21, radius: 0.09, depth: 0.05, seatRadius: 0.13, seatDepth: 0.06 },
    { x: 0, y: -0.37, z: -0.18, radius: 0.1, depth: 0.05, seatRadius: 0.145, seatDepth: 0.065 },
    { x: 0.36, y: -0.39, z: -0.21, radius: 0.09, depth: 0.05, seatRadius: 0.13, seatDepth: 0.06 },
  ],
};

/**
 * Pallbearer (skiff): a heavier hauler cab — chunkier, wider-set pillars, a lower
 * deeper brow, a broad dash with a steeper lip, beefier toed-in consoles and a
 * fat center stack. Still frames an open central view at the eye-point.
 */
export const SKIFF_COCKPIT_GEOMETRY: CockpitGeometry = {
  pillar: { width: 0.14, height: 2.0, depth: 0.16, spread: 0.66, rake: 0.2, y: 0.08, z: -0.78 },
  brow: {
    width: 1.56, height: 0.16, depth: 0.55, cornerWidth: 0.26, cornerHeight: 0.19, cornerDepth: 0.36,
    y: 0.5, z: -0.82,
  },
  dash: {
    width: 1.92, height: 0.36, depth: 0.9, y: -0.52, z: -0.62, lip: 0.52, lipDepth: 0.28,
    hoodWidth: 0.96, hoodHeight: 0.2, hoodDepth: 0.44, panelWidth: 0.31, panelHeight: 0.09,
    panelDepth: 0.34, panelInset: 0.07,
  },
  consoles: {
    width: 0.4, height: 0.78, depth: 0.95, x: 0.7, y: -0.4, z: -0.62, toe: 0.26,
    capHeight: 0.14, faceHeight: 0.2, faceDepth: 0.35,
  },
  centerStack: {
    // Heavier hauler hump, likewise trimmed + recessed behind the velocity bay.
    width: 0.36, height: 0.34, depth: 0.34, y: -0.34, z: -0.54,
  },
  // Matched bay set, mirroring the default cockpit with a slightly chunkier/
  // wider-set hauler profile (same shared bezel/pocket depths).
  bays: {
    left: {
      width: 0.14, height: 0.115, pocketDepth: 0.028, pitch: -0.42, yaw: 0.34,
      bezelThickness: 0.012, bezelHeight: 0.014, x: -0.285, y: -0.02, z: -0.31,
    },
    // Compact: narrowed + shortened and nudged toward centre so its left edge
    // (x - width/2 = -0.175) clears the POWER bay's right edge (~-0.215) — no
    // overlap — while staying left of the centred radar.
    centre: {
      width: 0.15, height: 0.064, pocketDepth: 0.028, pitch: -0.42, yaw: 0,
      bezelThickness: 0.012, bezelHeight: 0.014, x: -0.1, y: -0.065, z: -0.29,
    },
    right: {
      width: 0.14, height: 0.115, pocketDepth: 0.028, pitch: -0.42, yaw: -0.34,
      bezelThickness: 0.012, bezelHeight: 0.014, x: 0.285, y: -0.02, z: -0.31,
    },
  },
  bezels: [
    { x: -0.4, y: -0.4, z: -0.23, radius: 0.1, depth: 0.06, seatRadius: 0.145, seatDepth: 0.07 },
    { x: 0, y: -0.38, z: -0.19, radius: 0.11, depth: 0.06, seatRadius: 0.16, seatDepth: 0.075 },
    { x: 0.4, y: -0.4, z: -0.23, radius: 0.1, depth: 0.06, seatRadius: 0.145, seatDepth: 0.07 },
  ],
};

export const COCKPIT_CONFIGS: { default: CockpitConfig } & Record<string, CockpitConfig> = {
  default: {
    id: 'default',
    name: 'Cockpit',
    fov: 70,
    eyeOffset: [0, 0.02, 0.03],
    materials: BASE_MATERIALS,
    lights: BASE_LIGHTS,
    geometry: DEFAULT_COCKPIT_GEOMETRY,
  },
  skiff: {
    id: 'skiff',
    name: 'Pallbearer',
    fov: 70,
    eyeOffset: [0, 0.018, 0.035],
    materials: BASE_MATERIALS,
    lights: BASE_LIGHTS,
    geometry: SKIFF_COCKPIT_GEOMETRY,
  },
};

export const getCockpitConfig = (shipId: string): CockpitConfig =>
  COCKPIT_CONFIGS[shipId] ?? COCKPIT_CONFIGS.default;
